/* eslint-disable @typescript-eslint/no-explicit-any */
import { FilterOperator } from 'generated/graphql';
import _ from 'lodash';
import {
  CreateParams,
  CreateResult,
  DataProvider,
  DeleteManyParams,
  DeleteManyResult,
  DeleteParams,
  DeleteResult,
  fetchUtils,
  GetListParams,
  GetListResult,
  GetManyParams,
  GetManyReferenceParams,
  GetManyReferenceResult,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  UpdateManyParams,
  UpdateManyResult,
  UpdateParams,
  UpdateResult,
} from 'react-admin';

import { callApi } from './providerUtils';

export type DataProviderOperation =
  | 'create'
  | 'delete'
  | 'deleteMany'
  | 'getList'
  | 'getMany'
  | 'getManyReference'
  | 'getOne'
  | 'update'
  | 'updateMany';

const operatorMap: Record<string, FilterOperator> = {
  contains: FilterOperator.Contains,
  eq: FilterOperator.Equal,
  fts: FilterOperator.FulltextSearch,
  gt: FilterOperator.GreaterThan,
  gte: FilterOperator.GreaterThanOrEqual,
  ilike: FilterOperator.Ilike,
  in: FilterOperator.In,
  isNotNull: FilterOperator.IsNotNull,
  isNull: FilterOperator.IsNull,
  like: FilterOperator.Like,
  lt: FilterOperator.LessThan,
  lte: FilterOperator.LessThanOrEqual,
  ne: FilterOperator.NotEqual,
  nin: FilterOperator.NotIn,
  uailike: FilterOperator.UnaccentedIlike,
  ualike: FilterOperator.UnaccentedLike,
};

export abstract class BaseResourceDataProvider implements DataProvider {
  protected abstract getFields(resource: string, operation: DataProviderOperation): string;

  protected getInputFieldName(resource: string, operation: DataProviderOperation): string {
    switch (operation) {
      case 'create':
        return this.getCreateInputParamName(resource);
      case 'update':
      case 'updateMany':
        return this.getUpdateInputParamName(resource);
      default:
        throw new Error('Unsupported operation');
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected getDataFieldsToExclude(resource: string, operation: DataProviderOperation, params: any): string[] {
    return [
      '__typename',
      'creationTimestamp',
      'id',
      'updateTimestamp',
      this.getCreateInputParamName(resource),
      this.getUpdateInputParamName(resource),
    ];
  }

  protected transformParams(resource: string, operation: DataProviderOperation, params: any): any {
    switch (operation) {
      case 'getList':
      case 'getMany':
      case 'getManyReference':
        return {
          filterInput: this.getFilterInput(resource, operation, params),
          pageInput: this.getPageInput(resource, operation, params),
          sortInput: this.getSortInput(resource, operation, params),
        };
      case 'create':
      case 'update':
      case 'updateMany':
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { ...remainingData } = _.omit(params.data, this.getDataFieldsToExclude(resource, operation, params));
        const inputFieldName = this.getInputFieldName(resource, operation);
        return { ...params, [inputFieldName]: remainingData };
      default:
        return { ...params };
    }
  }

  protected getReturnFragment(resource: string, operation: DataProviderOperation): string {
    const fields = this.getFields(resource, operation);
    switch (operation) {
      case 'getList':
      case 'getMany':
      case 'getManyReference':
        return `{
          items {
            ${fields}
          }
        totalItems
        }`;
      case 'delete':
      case 'deleteMany':
        return '';
      default:
        return `{
          ${fields}
        }`;
    }
  }

  protected getParamTypes(resource: string, operation: DataProviderOperation): Record<string, string> {
    switch (operation) {
      case 'getList':
        return {
          filterInput: 'FilterInput',
          pageInput: 'PageInput',
          sortInput: 'SortInput',
        };
      case 'getMany':
        return {
          filterInput: 'FilterInput',
        };
      case 'getManyReference':
        return {
          filterInput: 'FilterInput',
          pageInput: 'PageInput',
          sortInput: 'SortInput',
        };
      case 'create':
        return {
          [this.getCreateInputParamName(resource)]: `${resource}Input!`,
        };
      case 'update':
        return {
          id: 'ID!',
          [this.getUpdateInputParamName(resource)]: `${resource}Input!`,
        };
      case 'updateMany':
        return {
          ids: '[ID!]!',
          [this.getUpdateInputParamName(resource)]: `${resource}Input!`,
        };
      case 'delete':
        return {
          id: `ID!`,
        };
      case 'deleteMany':
        return {
          ids: '[ID!]!',
        };
      case 'getOne':
        return {
          id: 'ID!',
        };
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected getCreateInputParamName(resource: string): string {
    return resource[0].toLowerCase() + resource.slice(1);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected getUpdateInputParamName(resource: string): string {
    const name = this.getCreateInputParamName(resource);
    return 'updated' + name[0].toUpperCase() + name.slice(1);
  }

  protected getResourcePlural(resource: string): string {
    return resource.endsWith('s') ? resource + 'es' : resource + 's';
  }

  protected transformResults(
    resource: string,
    operation: DataProviderOperation,
    operationName: string,
  ): (result: any) => any {
    switch (operation) {
      case 'getList':
      case 'getMany':
      case 'getManyReference':
        return (result: any) => ({
          data: result.data[operationName].items,
          total: result.data[operationName].totalItems,
        });
      default:
        return (result: any) => ({
          data: result.data[operationName],
        });
    }
  }

  protected getOperationName(resource: string, operation: DataProviderOperation): string {
    const operationName = operation.toString();
    const resources = this.getResourcePlural(resource);
    switch (operation) {
      case 'getOne':
        return `get${resource}`;
      case 'getList':
      case 'getMany':
      case 'getManyReference':
        return `get${resources}Page`;
      case 'deleteMany':
        return `delete${resources}`;
      case 'updateMany':
        return `update${resources}`;
      default:
        return `${operationName}${resource}`;
    }
  }

  protected getFilterInputArguments(resource: string, operation: DataProviderOperation, params: any): any[] {
    const filter = params?.filter ?? [];
    const filterConditions = [
      ...Object.keys(filter).map((key) => {
        if (key.startsWith('filterCondition')) {
          // own filter (e.g. for autocomplete returnfragment)
          return { filterCondition: filter[key] };
        } else {
          // default filtering by value
          if (typeof filter[key] == 'object') {
            const flattenedFilter = fetchUtils.flattenObject(filter[key]);
            const flattenedKey = Object.keys(flattenedFilter)[0];
            const values = [flattenedFilter[flattenedKey]];
            const [fieldPath, operatorName] = `${key}.${flattenedKey}`.split('__');
            const operator = operatorMap[operatorName] ?? FilterOperator.Equal;
            return {
              filterCondition: {
                fieldPath: fieldPath,
                operator: operator,
                values: values,
              },
            };
          } else {
            const [fieldPath, operatorName] = key.split('__');
            const operator = operatorMap[operatorName] ?? FilterOperator.Equal;
            const values = [filter[key]];
            return {
              filterCondition: {
                fieldPath: fieldPath,
                operator: operator,
                values: values,
              },
            };
          }
        }
      }),
    ];
    switch (operation) {
      case 'getMany':
        filterConditions.push({
          filterCondition: {
            fieldPath: 'id',
            operator: FilterOperator.In,
            values: params.ids,
          },
        });
        return filterConditions;
      case 'getManyReference':
        filterConditions.push({
          filterCondition: {
            fieldPath: params.target,
            operator: FilterOperator.In,
            values: [params.id],
          },
        });
        return filterConditions;
      default:
        return filterConditions;
    }
  }

  protected getFilterInput(resource: string, operation: DataProviderOperation, params: any): any {
    const filterInputArguments = this.getFilterInputArguments(resource, operation, params).filter(
      (item) => item !== null,
    );
    return {
      arguments: filterInputArguments,
    };
  }

  protected getPageInput(resource: string, operation: DataProviderOperation, params: any): any {
    switch (operation) {
      case 'getList':
      case 'getMany':
      case 'getManyReference':
        return params.pagination
          ? {
              pageIndex: params.pagination.page - 1,
              pageSize: params.pagination.perPage,
            }
          : null;
      default:
        return null;
    }
  }

  private extractFieldPath(field: string): string {
    const fieldPattern = /^\[(.*?)\]$/;
    return fieldPattern.test(field) ? field.replace(fieldPattern, '$1') : field;
  }

  protected getSortInput(resource: string, operation: DataProviderOperation, params: any): any {
    switch (operation) {
      case 'getMany':
      case 'getManyReference':
      case 'getList':
        return params.sort
          ? {
              by: [{ direction: params.sort.order, fieldPath: this.extractFieldPath(params.sort.field) }],
            }
          : null;
      default:
        return null;
    }
  }

  protected getOperationType(resource: string, operation: DataProviderOperation): 'query' | 'mutation' {
    switch (operation) {
      case 'create':
      case 'delete':
      case 'deleteMany':
      case 'update':
      case 'updateMany':
        return 'mutation';
      default:
        return 'query';
    }
  }

  protected performOperation(resource: string, operation: DataProviderOperation, params: any): Promise<any> {
    const operationName = this.getOperationName(resource, operation);
    const returnFragment = this.getReturnFragment(resource, operation);
    const paramTypes = this.getParamTypes(resource, operation);
    const operationType = this.getOperationType(resource, operation);
    const variables = this.transformParams(resource, operation, params);
    const resultTransformer = this.transformResults(resource, operation, operationName);
    return callApi(operationType, operationName, paramTypes, variables, returnFragment, resultTransformer);
  }

  /*
   * Create a record
   */
  public async create(resource: string, params: CreateParams): Promise<CreateResult> {
    return this.performOperation(resource, 'create', params);
  }

  /*
   * Delete a record
   */
  public async delete(resource: string, params: DeleteParams): Promise<DeleteResult> {
    return this.performOperation(resource, 'delete', params);
  }

  /*
   * Delete many records
   */

  public async deleteMany(resource: string, params: DeleteManyParams): Promise<DeleteManyResult> {
    return this.performOperation(resource, 'deleteMany', params);
  }

  /*
   * Get list of records
   */
  public async getList(resource: string, params: GetListParams): Promise<GetListResult> {
    return this.performOperation(resource, 'getList', params);
  }

  /*
   * Get many records
   */
  public async getMany(resource: string, params: GetManyParams): Promise<GetManyResult> {
    return this.performOperation(resource, 'getMany', params);
  }

  /*
   * Get many records by reference
   */
  public async getManyReference(resource: string, params: GetManyReferenceParams): Promise<GetManyReferenceResult> {
    return this.performOperation(resource, 'getManyReference', params);
  }

  /*
   * Get a record
   */
  public async getOne(resource: string, params: GetOneParams): Promise<GetOneResult> {
    return this.performOperation(resource, 'getOne', params);
  }

  /*
   * Update a record
   */
  public async update(resource: string, params: UpdateParams): Promise<UpdateResult> {
    return this.performOperation(resource, 'update', params);
  }

  /*
   * Update many records
   */
  public async updateMany(resource: string, params: UpdateManyParams): Promise<UpdateManyResult> {
    return this.performOperation(resource, 'updateMany', params);
  }
}
